import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import {
  setSearchString,
  handleSubmitSearch,
  handleClearSearch,
  submitSearchStart,
} from 'actions/search'
import { getSearchStringByCollection } from 'selectors/search'
import strings from 'utils/strings'
import { Header } from 'components/patterns'
import {
  SearchInputField,
  SearchInfoIcon,
  SearchWrapper,
  SearchLabel,
} from './SearchInput.style'

const SearchInput = ({
  className,
  collectionName,
  tooltip,
  label,
  labelType,
  width,
  onSearch,
  addToSearchRequestBody,
  onClear,
  fetchResults = true,
}) => {
  const dispatch = useDispatch()
  const searchString = useSelector(getSearchStringByCollection, collectionName)
  const [searchValue, setSearchValue] = useState(searchString)
  const prevSearchStringRef = useRef(searchString)

  // Bug 486891 Issue 10: Sync local state when Redux search is externally cleared
  // Also sync when Redux transitions from empty to non-empty (e.g. URL-seeded contextual search)
  useEffect(() => {
    if (prevSearchStringRef.current !== '' && searchString === '') {
      setSearchValue('')
    } else if (prevSearchStringRef.current === '' && searchString !== '') {
      setSearchValue(searchString)
    }
    prevSearchStringRef.current = searchString
  }, [searchString])

  const handleChange = (event) => {
    setSearchValue(event.target.value)
  }

  const handleOnSearch = () => {
    dispatch(setSearchString(searchValue, collectionName))
    onSearch(searchValue)
    if (fetchResults) {
      dispatch(
        handleSubmitSearch(
          collectionName,
          true,
          fetchResults,
          addToSearchRequestBody
        )
      )
    }
  }

  const handleOnClear = () => {
    setSearchValue('')
    onClear()
    dispatch(handleClearSearch(collectionName))
    dispatch(submitSearchStart(false))
  }

  const hasLabel = !!label || false

  return (
    <SearchWrapper hasLabel={hasLabel}>
      {hasLabel && (
        <SearchLabel labelType={labelType}>
          {labelType !== 'label' ? (
            <Header type="h3">{label}</Header>
          ) : (
            <label htmlFor={`${collectionName}-search`}>{label}</label>
          )}{' '}
          {tooltip && (
            <SearchInfoIcon
              overlayClassName="autoWidth"
              tooltipPosition="right"
              icon="question_mark_tooltip"
              tooltip={tooltip}
            />
          )}
        </SearchLabel>
      )}
      <SearchInputField
        className={className}
        size="large"
        id={`${collectionName}-search`}
        value={searchValue}
        onChange={handleChange}
        onClear={handleOnClear}
        onSearch={handleOnSearch}
        placeholder={strings.enter_search_term}
        width={width}
      />
      {!hasLabel && tooltip && (
        <SearchInfoIcon topRight tooltip={tooltip} icon={'search'} />
      )}
    </SearchWrapper>
  )
}

SearchInput.defaultProps = {
  onSearch: () => {},
  onClear: () => {},
}

SearchInput.propTypes = {
  className: PropTypes.string,
  collectionName: PropTypes.string,
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  tooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.element,
    PropTypes.func,
  ]),
  label: PropTypes.string,
  labelType: PropTypes.oneOf(['header', 'label']),
  width: PropTypes.string,
  fetchResults: PropTypes.bool,
  addToSearchRequestBody: PropTypes.shape({
    serial: PropTypes.string,
  }),
}

export default SearchInput
